import { useMutation } from '@tanstack/react-query'
import { notificationsMutationKeys, notificationsQueryKeys } from '../constants'
import api from '../../config/axios-config'
import { customQueryClient } from '../../config/query-client'

/**
 * Updates email frequency settings for notification emails.
 * @param {Object} options - Additional options for `useMutation`.
 * @returns {Object} - Mutation result. Call `mutate({ emailFrequency })` with the desired frequency value.
 * Endpoint:
 * Frontend proxy: PATCH /api/notificationSettings
 * Mutation payload:
 * `emailFrequency`: frequency value accepted by the notification settings API.
 * Important details:
 * On success this hook updates the `NOTIFICATIONS_GET_EMAIL_FREQUENCY` cache to avoid an extra fetch.
 */

export const useUpdateEmailFrequencyMutation = (options) => {
  const handleMutation = async ({ emailFrequency }) => {
    const config = {
      method: 'patch',
      data: { emailFrequency }
    }

    const { data } = await api(`/api/notificationSettings`, config)

    return data
  }

  const mergedOptions = { ...options }
  const customOnSuccess = options?.onSuccess

  delete mergedOptions.onSuccess

  return useMutation({
    mutationKey: [
      notificationsMutationKeys.NOTIFICATIONS_UPDATE_EMAIL_FREQUENCY
    ],
    mutationFn: handleMutation,
    onSuccess: (data, variables, context) => {
      customQueryClient.setQueryData(
        [notificationsQueryKeys.NOTIFICATIONS_GET_EMAIL_FREQUENCY],
        data
      )

      customOnSuccess?.(data, variables, context)
    },
    ...mergedOptions
  })
}
